import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Upload, Video, Check, Loader2, FolderOpen, X } from 'lucide-react';
import { useMediaLibrary } from '@/hooks/useMediaLibrary';
import { MediaBucket } from '@/lib/secureMediaUpload';
import { uploadVideoSecure, getMediaUrl } from '@/lib/secureMediaUpload';
import type { UploadStageType } from '@/components/admin/UploadProgress';
import { UploadProgress } from '@/components/admin/UploadProgress';
import { cn } from '@/lib/utils';

interface VideoPickerProps {
  value?: string; // path or URL
  onChange: (value: string, path: string) => void;
  bucketType: MediaBucket;
  label?: string;
  placeholder?: string;
  className?: string;
}

export function VideoPicker({
  value,
  onChange,
  bucketType,
  label = 'Video',
  placeholder = 'Select or upload a video',
  className,
}: VideoPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPath, setSelectedPath] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingLocal, setUploadingLocal] = useState(false);
  const [uploadProgressLocal, setUploadProgressLocal] = useState({ stage: 'idle', progress: 0, error: '' });

  const { files, loading, loadFiles } = useMediaLibrary({ bucketType });

  useEffect(() => {
    if (value) {
      if (value.startsWith('http')) {
        setPreviewUrl(value);
        setUrlInput(value);
      } else {
        const url = getMediaUrl(value, bucketType);
        setPreviewUrl(url);
        setSelectedPath(value);
      }
    }
  }, [value, bucketType]);

  useEffect(() => {
    if (isOpen) loadFiles();
  }, [isOpen, loadFiles]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingLocal(true);
      setUploadProgressLocal({ stage: 'uploading', progress: 0, error: '' });
      const result = await uploadVideoSecure(file, bucketType);
      onChange(result.url, result.path);
      setSelectedPath(result.path);
      setPreviewUrl(result.url);
      setIsOpen(false);
    } catch (err: unknown) {
      console.error('Video upload error:', err);
      const message = err instanceof Error ? err.message : String(err);
      setUploadProgressLocal({ stage: 'error', progress: 0, error: message });
    } finally {
      setUploadingLocal(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSelectExisting = (file: { path: string; url: string; mime_type?: string | null }) => {
    // Only allow videos from library
    if (!file.mime_type || !file.mime_type.startsWith('video')) return;
    setSelectedPath(file.path);
    setPreviewUrl(file.url);
    onChange(file.url, file.path);
    setIsOpen(false);
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      setPreviewUrl(urlInput);
      onChange(urlInput, '');
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setPreviewUrl('');
    setSelectedPath('');
    setUrlInput('');
    onChange('', '');
  };

  return (
    <div className={cn('space-y-2', className)}>
      {label && <label className="text-sm font-medium">{label}</label>}

      <div className="relative">
        {previewUrl ? (
          <div className="relative w-full h-40 rounded-lg overflow-hidden border border-border bg-muted">
            <video src={previewUrl} className="w-full h-full object-cover" muted loop playsInline preload="metadata" />
            <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Button type="button" size="sm" variant="secondary" onClick={() => setIsOpen(true)}>Change</Button>
              <Button type="button" size="sm" variant="destructive" onClick={handleClear}><X className="w-4 h-4" /></Button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setIsOpen(true)} className="w-full h-40 rounded-lg border-2 border-dashed border-border hover:border-primary/50 bg-muted/30 flex flex-col items-center justify-center gap-2 transition-colors">
            <Video className="w-8 h-8 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{placeholder}</span>
          </button>
        )}
      </div>

      {uploadingLocal && uploadProgressLocal.stage !== 'idle' && uploadProgressLocal.stage !== 'complete' && (
        <UploadProgress progress={uploadProgressLocal.progress} stage={uploadProgressLocal.stage as UploadStageType} error={uploadProgressLocal.error} />
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent aria-describedby="video-picker-dialog-desc" className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Video</DialogTitle>
            <p id="video-picker-dialog-desc" className="text-sm text-muted-foreground">
              Upload MP4, MOV, or WEBM video files. The selected file will be used for this portfolio item.
            </p>
          </DialogHeader>

          <Tabs defaultValue="upload" className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="upload"><Upload className="w-4 h-4 mr-2" />Upload</TabsTrigger>
              <TabsTrigger value="library"><FolderOpen className="w-4 h-4 mr-2" />Library</TabsTrigger>
              <TabsTrigger value="url"><Video className="w-4 h-4 mr-2" />URL</TabsTrigger>
            </TabsList>

            <TabsContent value="upload" className="flex-1 flex flex-col">
              <div className="flex-1 flex flex-col items-center justify-center p-8 border-2 border-dashed border-border rounded-lg mt-4">
                {uploadingLocal ? (
                  <div className="w-full max-w-xs">
                    <UploadProgress progress={uploadProgressLocal.progress} stage={uploadProgressLocal.stage as UploadStageType} error={uploadProgressLocal.error} />
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4 text-center">Drag and drop an MP4 or WEBM video here, or click to select</p>
                    <Button type="button" onClick={() => fileInputRef.current?.click()}>Upload Video</Button>
                    <input ref={fileInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" onChange={handleFileSelect} className="hidden" />
                    <p className="text-xs text-muted-foreground mt-4">MP4, MOV, WEBM • Max 250MB</p>
                  </>
                )}
              </div>
            </TabsContent>

            <TabsContent value="library" className="flex-1 min-h-0 mt-4">
              {loading ? (
                <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
              ) : files.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><FolderOpen className="w-12 h-12 mb-4" /><p>No videos in library</p><p className="text-sm">Upload videos to see them here</p></div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 p-1">
                    {files.filter(f => f.mime_type && f.mime_type.startsWith('video')).map((file) => (
                      <button key={file.path} type="button" onClick={() => handleSelectExisting(file)} className={cn('relative aspect-square rounded-lg overflow-hidden border-2 transition-all', selectedPath === file.path ? 'border-primary ring-2 ring-primary/20' : 'border-transparent hover:border-primary/50')}>
                        <video src={file.url} className="w-full h-full object-cover" muted loop playsInline preload="metadata" />
                        {selectedPath === file.path && (<div className="absolute inset-0 bg-primary/20 flex items-center justify-center"><div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center"><Check className="w-5 h-5 text-primary-foreground" /></div></div>)}
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>

            <TabsContent value="url" className="flex-1 mt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Video URL</label>
                  <Input placeholder="https://example.com/video.mp4" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} />
                </div>

                {urlInput && (
                  <div className="h-40 rounded-lg overflow-hidden border border-border bg-muted flex items-center justify-center">
                    <video src={urlInput} className="w-full h-full object-cover" muted loop playsInline preload="metadata" />
                  </div>
                )}

                <Button type="button" onClick={handleUrlSubmit} disabled={!urlInput.trim() || !/(mp4|webm|mov)(\?|$)/i.test(urlInput)} className="w-full">Use This URL</Button>
                {!/(mp4|webm|mov)(\?|$)/i.test(urlInput) && urlInput.trim() && (
                  <p className="text-xs text-destructive mt-2">Please provide a direct MP4, MOV, or WEBM video URL.</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
